import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useApp } from '../../contexts/AppContext';
import './Auth.css';

// Google icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function SignupPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const supabase = useSupabaseClient();
  const { signup } = useApp();
  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword(show => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(show => !show);

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value) return 'דוא"ל הוא שדה חובה';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'כתובת דוא"ל לא תקינה';
        break;
      case 'password':
        if (!value) return 'סיסמה היא שדה חובה';
        if (value.length < 8) return 'הסיסמה חייבת להכיל לפחות 8 תווים';
        break;
      case 'confirmPassword':
        if (!value) return 'אימות סיסמה הוא שדה חובה';
        if (value !== formData.password) return 'הסיסמאות אינן תואמות';
        break;
      default:
        break;
    }
    return null;
  };

  const handleChange = (name, value) => {
    setFieldErrors(prev => ({ ...prev, [name]: null }));
    setError('');

    setFormData(prev => ({ ...prev, [name]: value }));

    const fieldError = validateField(name, value);
    if (fieldError) {
      setFieldErrors(prev => ({ ...prev, [name]: fieldError }));
    }

    if (name === 'password' && formData.confirmPassword) {
      const confirmError = validateField('confirmPassword', formData.confirmPassword);
      if (confirmError) {
        setFieldErrors(prev => ({ ...prev, confirmPassword: confirmError }));
      }
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (loading) return;

    console.log('Starting form submission...', formData);

    const errors = {};
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        console.log(`Validation error for ${field}:`, error);
        errors[field] = error;
      }
    });

    if (Object.keys(errors).length > 0) {
      console.log('Form validation failed:', errors);
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { email, password } = formData;
      console.log('Form validation passed, attempting signup...');
      const result = await signup(email, password);
      console.log('Signup result:', result);

      if (result?.error) {
        throw result.error;
      }

      // If we get here, signup was successful - go to wizard to complete profile
      console.log('Signup successful, navigating to wizard');
      navigate('/wizard');
    } catch (err) {
      console.error('Form submission error:', {
        message: err.message,
        code: err.code,
        details: err.details,
      });

      // Handle specific error cases
      if (err.message?.includes('Maximum signup attempts reached')) {
        setError('מספר נסיונות ההרשמה הגיע למקסימום. נא לנסות שוב מאוחר יותר.');
      } else if (err.message?.includes('rate limit')) {
        setError('נא להמתין מספר דקות לפני נסיון נוסף');
      } else if (err.message?.includes('User already registered')) {
        setError('כתובת האימייל כבר רשומה במערכת');
      } else if (err.message?.includes('Invalid email')) {
        setError('כתובת אימייל לא תקינה');
      } else if (err.message?.includes('Password')) {
        setError('סיסמה לא תקינה - יש להשתמש ב-8 תווים לפחות');
      } else {
        setError(err.message || 'אירעה שגיאה בתהליך ההרשמה. נא לנסות שוב.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google OAuth signup
  const handleGoogleSignup = async () => {
    try {
      setGoogleLoading(true);
      setError('');
      const redirectUrl =
        process.env.NODE_ENV === 'production'
          ? 'https://www.kamuyot.com/auth/callback'
          : `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        },
      });
      if (error) throw error;
    } catch (err) {
      console.error('Google signup error:', err);
      setError('שגיאה בהרשמה עם Google');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="auth-container login-background">
      <div className="auth-box">
        <h2>הרשמה</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>אימייל</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => handleChange('email', e.target.value)}
              required
            />
            {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
          </div>

          <div className="form-group password-input-container">
            <label>סיסמה</label>
            <div className="input-with-icon">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={e => handleChange('password', e.target.value)}
                required
              />
              <button
                type="button"
                onClick={handleClickShowPassword}
                className="password-toggle"
                aria-label="toggle password visibility"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
          </div>

          <div className="form-group password-input-container">
            <label>אימות סיסמה</label>
            <div className="input-with-icon">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={e => handleChange('confirmPassword', e.target.value)}
                required
              />
              <button
                type="button"
                onClick={handleClickShowConfirmPassword}
                className="password-toggle"
                aria-label="toggle password visibility"
              >
                {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {fieldErrors.confirmPassword && (
              <span className="field-error">{fieldErrors.confirmPassword}</span>
            )}
          </div>

          <button disabled={loading} type="submit" className="login-button">
            {loading ? 'נרשם...' : 'הרשמה'}
          </button>
        </form>

        <div className="auth-divider">
          <span>או</span>
        </div>

        <button
          type="button"
          className="google-button"
          onClick={handleGoogleSignup}
          disabled={googleLoading}
        >
          <GoogleIcon />
          {googleLoading ? 'נרשם...' : 'הירשם עם Google'}
        </button>

        <div className="auth-link">
          <Link to="/login">כבר יש לך חשבון? התחבר</Link>
        </div>

        <div className="legal-links">
          <Link to="/terms">תנאי שימוש</Link>
          <span>|</span>
          <Link to="/privacy">מדיניות פרטיות</Link>
        </div>
      </div>
    </div>
  );
}
